import { useNavigate } from 'react-router-dom';
import { showToastFromSnackbar } from '@/utils/showToast';
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from '@/stores/userStore';
import { useLocales } from '@/locales';
import { useApiMutation } from '@/hooks/useApi';
import authApi from '@/api/authApi';
import { AUTH_ROUTES, USER_ROLES } from '@/pages/auth/constants/authConstants';
import { useAuthFlowStore } from '@/pages/auth/stores/authFlowStore';
import {
  validateClientForm,
  checkRateLimit,
} from '@/pages/auth/utils/validation';

/**
 * Custom hook for client authentication via test login endpoint
 */
export const useClientAuth = () => {
  const navigate = useNavigate();

  const { translate } = useLocales();
  const setUser = useUserStore(state => state.setUser);

  const {
    clientState,
    setClientCredentials,
    setClientErrors,
    setClientLoading,
    resetClientState,
    getCanSubmitClient,
    addAuthAttempt,
  } = useAuthFlowStore();

  const {
    credentials,
    errors,
    isLoading: storeLoading,
    lastSubmissionTime,
  } = clientState;

  const loginMutation = useApiMutation(
    payload => authApi.clientLogin(payload),
    {
      publishErrors: false,
      onSuccess: result => {
        handleLoginSuccess(result);
      },
      onError: error => {
        handleLoginError(error);
      },
    },
  );

  const handleCredentialChange = field => event => {
    const value = event.target.value;
    setClientCredentials(field, value);

    if (loginMutation.error) {
      loginMutation.reset();
    }
  };

  const handleLoginSuccess = result => {
    setClientLoading(false);

    addAuthAttempt({
      type: 'client',
      success: true,
      TIN: credentials.TIN,
    });

    resetClientState();

    const { token, user: userData } = result.data || {};

    if (!token || !userData?._id) {
      showToastFromSnackbar(
        translate('systemError') || 'Unexpected response from server',
        {
          variant: 'error',
        },
      );
      return;
    }

    const user = {
      id: userData._id,
      role: userData.role || USER_ROLES.USER,
      isVerified: true,
      details: userData,
      token,
      socketUUID: uuidv4(),
    };

    setUser(user);

    showToastFromSnackbar(translate('Login_Successful'), {
      variant: 'success',
    });

    setTimeout(() => {
      navigate(AUTH_ROUTES.USER_SUBMISSIONS, { replace: true });
    }, 1000);
  };

  const handleLoginError = error => {
    setClientLoading(false);

    addAuthAttempt({
      type: 'client',
      success: false,
      TIN: credentials.TIN,
      error: error.message,
    });

    const details = error.details || {};
    const fieldErrors = {
      TIN: !!details.TIN,
      contractId: !!details.contractId,
      alias: !!details.alias,
    };

    if (fieldErrors.TIN || fieldErrors.contractId || fieldErrors.alias) {
      setClientErrors(fieldErrors);
    }

    showToastFromSnackbar(
      error.message ||
        translate('Check_credentials_again') ||
        'Please verify your credentials and try again.',
      {
        variant: 'error',
      },
    );
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const rateLimitCheck = checkRateLimit(lastSubmissionTime);
    if (!rateLimitCheck.allowed) {
      showToastFromSnackbar(translate('Please wait before trying again'), {
        variant: 'warning',
      });
      return;
    }

    const validation = validateClientForm(credentials);
    if (!validation.isValid) {
      setClientErrors(validation.fieldErrors);
      showToastFromSnackbar(translate('message_7'), {
        variant: 'warning',
      });
      return;
    }

    const payload = {
      TIN: credentials.TIN,
      contractId: credentials.contractId,
      alias: credentials.alias,
    };

    setClientLoading(true);
    loginMutation.mutate(payload);
  };

  const resetForm = () => {
    resetClientState();
    loginMutation.reset();
  };

  return {
    credentials,
    errors,
    isLoading: loginMutation.isPending || storeLoading,
    error: loginMutation.error,
    canSubmit: getCanSubmitClient() && !loginMutation.isPending,
    rateLimitInfo: checkRateLimit(lastSubmissionTime),
    handleCredentialChange,
    handleSubmit,
    resetForm,
  };
};

export default useClientAuth;
